import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '@/components/Loading/Loader';
import { NotFound } from '@/components/Message/NotFound';
import { RepairServiceInfosManagement } from '../components/RepairServiceInfosManagement';
import {
  TRepairServiceSchema,
  repairServiceSchema,
} from '@/schemas/repair-service/repair-service.schema';
import {
  useGetRepairServiceById,
  useUpdateRepairService,
} from '@/hooks/repair-service.hooks';
import { useTranslation } from 'react-i18next';
import { getLanguage } from '@/utils/language.utils';

export const EditRepairServicePage = () => {
  const { id } = useParams();
  const { data, isLoading } = useGetRepairServiceById(`${id}`);
  const { t } = useTranslation('repairService');
  const navigate = useNavigate();

  const {
    watch: watchRepairService,
    control: repairServiceControl,
    register: registerRepairService,
    setValue: setRepairServiceValue,
    setError: setRepairServiceError,
    handleSubmit: handleSubmitRepairService,
    formState: {
      errors: repairServiceErrors,
      isSubmitting: isSubmittingRepairService,
    },
  } = useForm<TRepairServiceSchema>({
    resolver: zodResolver(repairServiceSchema),
  });

  const {
    mutate: mutateUpdateRepairService,
    isPending: isPendingUpdateRepairService,
  } = useUpdateRepairService({
    t,
    handleSuccess: () => navigate('/repair-services'),
  });

  const onSubmitRepairServiceInfos = (repairService: TRepairServiceSchema) => {
    const { languageCode } = getLanguage();

    const dataWithLanguageCode = {
      ...repairService,
      languageCode,
    };

    const repairServiceWithId = { id, ...dataWithLanguageCode };
    mutateUpdateRepairService(repairServiceWithId);
  };

  return (
    <>
      <PageHeader
        backToLink="/repair-services"
        title={t('theRepairService')}
        subTitle={data?.data?.name}
        subTitleIconClassName="ki-paintbucket"
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem
              text={t('repairServiceList')}
              linkTo="/repair-services"
            />
            <BreadcrumbItem text={data?.data?.name} active />
          </BreadcrumbList>
        }
      />
      {!isLoading && !data?.data ? (
        <NotFound text={`${t('repairServiceNotFound')}...`} />
      ) : (
        <div>
          <RepairServiceInfosManagement<TRepairServiceSchema>
            t={t}
            data={!isLoading && data?.data}
            watch={watchRepairService}
            control={repairServiceControl}
            register={registerRepairService}
            setValue={setRepairServiceValue}
            setError={setRepairServiceError}
            errors={repairServiceErrors}
            submitDisabled={
              isSubmittingRepairService || isPendingUpdateRepairService
            }
            onSubmit={handleSubmitRepairService(onSubmitRepairServiceInfos)}
          />
        </div>
      )}
      {(isLoading ||
        isSubmittingRepairService ||
        isPendingUpdateRepairService) && <Loader />}
    </>
  );
};
